/* import __COLOCATED_TEMPLATE__ from './main.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { StandaloneCompanyAttribute } from 'embercom/objects/standalone/data-fields/common';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type ModalService from 'embercom/services/modal-service';
import type Attribute from 'embercom/models/attribute';
import type EmberArray from '@ember/array';
import type AppService from 'embercom/services/app-service';
import type { StandalonePlatform } from 'embercom/objects/standalone/constants';

interface Args {
  archivedAttributes: Attribute[];
}

type AttributeWithSettings = { attribute: Attribute };
export default class CompanyAttributesMain extends Component<Args> {
  @service declare modalService: ModalService;
  @service declare attributeService: {
    displayableCompanyAttributes: AttributeWithSettings[];
    attributesWithSettings(attributes: Attribute[]): EmberArray<AttributeWithSettings>;
  };
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: {
    app: { hasStandalonePlatform: (platform: StandalonePlatform) => boolean };
  } & AppService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  get app() {
    return this.appService.app;
  }

  get attributes() {
    return this.unmappedStandaloneCustomCompanyAttributes.concat(
      this.standaloneSyncedCompanyAttributes,
      this.standaloneBuiltInCompanyAttributes,
    );
  }

  private get unmappedStandaloneCustomCompanyAttributes(): StandaloneCompanyAttribute[] {
    return this.attributeService.displayableCompanyAttributes
      .filter(
        (attributeWithSettings: { attribute: Attribute }) =>
          attributeWithSettings.attribute.isCustomData &&
          !this.standaloneSyncedCompanyAttributes.any(
            (standaloneCompanyAttribute) =>
              standaloneCompanyAttribute.intercomAttribute?.identifier ===
              attributeWithSettings.attribute.identifier,
          ),
      )
      .concat(this.archivedCompanyAttributes)
      .map(
        (attributeWithSettings: AttributeWithSettings) =>
          new StandaloneCompanyAttribute({
            intercomAttribute: attributeWithSettings.attribute,
          }),
      );
  }

  private get standaloneSyncedCompanyAttributes(): StandaloneCompanyAttribute[] {
    return this.standalonePlatformCompanyAttributes
      .filter((field) => field.synchronized && !field.builtIn)
      .map(
        (field) =>
          new StandaloneCompanyAttribute({
            standalonePlatform: this.standalonePlatform,
            field,
            intercomAttribute: this.findAttribute(field.intercomCdaIdentifier!),
          }),
      );
  }

  private findAttribute(identifier: string) {
    return this.attributeService.displayableCompanyAttributes.find(
      (attributeWithSettings: AttributeWithSettings) => {
        return attributeWithSettings.attribute.identifier === identifier;
      },
    )!.attribute;
  }

  private get standaloneBuiltInCompanyAttributes(): StandaloneCompanyAttribute[] {
    return this.standalonePlatformCompanyAttributes
      .filter((field) => field.builtIn)
      .map(
        (field) =>
          new StandaloneCompanyAttribute({
            standalonePlatform: this.standalonePlatform,
            field,
          }),
      );
  }

  private get standalonePlatform() {
    return this.appService.mainStandalonePlatform?.platformKey;
  }

  private get hasNonGenericStandalonePlatform(): boolean {
    return this.standalonePlatform && !this.app.hasStandalonePlatform('unknown');
  }

  private get standalonePlatformCompanyAttributes() {
    return this.appService.mainStandalonePlatform.companyFields;
  }

  private get archivedCompanyAttributes() {
    // @ts-expect-error EmberArray#filterBy is not typed correctly but does support dependent keys syntax.
    return this.archivedAttributesWithSettings.filterBy('attribute.isCompany');
  }

  private get archivedAttributesWithSettings(): EmberArray<AttributeWithSettings> {
    return this.attributeService.attributesWithSettings(this.args.archivedAttributes);
  }

  get emptyStateDescription() {
    if (this.hasNonGenericStandalonePlatform) {
      return this.intl.t(
        `standalone.settings.data.companies.description.${this.standalonePlatform}`,
      );
    }

    return;
  }

  get emptyStateButtonLabel() {
    if (this.hasNonGenericStandalonePlatform) {
      return this.intl.t(
        `standalone.settings.data.companies.sync-via-platform.${this.standalonePlatform}`,
      );
    }

    return this.intl.t('new-settings.data.companies.create-attribute');
  }

  @action
  emptyStateOnButtonClick() {
    if (this.hasNonGenericStandalonePlatform) {
      return this.router.transitionTo('apps.app.settings.standalone.integration');
    }

    return this.openAttributeCreationModal();
  }

  @action
  openAttributeCreationModal() {
    this.modalService.openModal('settings/modals/attribute-details', {
      attribute: null,
      allowArchival: false,
      showPeopleAndCompanyToggleTab: false,
      typeToCreate: 'company',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::DataAttributes::Company::Main': typeof CompanyAttributesMain;
    'standalone/company-attributes/main': typeof CompanyAttributesMain;
  }
}
